/** @shell standard @header bar */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { View, Text, StyleSheet, Alert, Platform } from 'react-native';
import Animated from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRoute } from '@react-navigation/native';
import { ScreenHeader, useHeaderTopPadding, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { Clickable } from '../components/common/Clickable';
import { SvgIcon } from '../components/common/SvgIcon';
import { SolidCtaClickable } from '../components/common/SolidCtaClickable';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import { hexToRgba } from '../shared/theme/colorUtils';
import {
  postUpgradeSubscription,
  getSubscriptionUpgradableFoodTypes,
  getSubscriptionUpgradeDayLeft,
  canUpgradeSubscription,
  buildUpgradeFoodTypesPayload,
  type SubscriptionData,
  type UpgradableFoodType,
} from '../data/domains/subscription';
import { useAuthStore } from '../shared/auth/store';
import { useAuthNavigation, useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { useTranslation, getApiText, formatNumber } from '../shared/i18n';
import { useApiConfig, resolveOrganizationPaymentMode } from '../data/domains/config';
import { formatCurrency } from '../shared/utils/currencyUtils';
import { EmptyState } from '../components/common/EmptyState';
import {
  StackScreenShell,
  STACK_SCROLL_GUTTER,
  STACK_SECTION_GAP,
  studioCardShell,
  DASH_STUDIO_RADIUS,
  androidScaleFont,
  androidScaleSize,
  androidFlatSurfaceStyle,
} from '../shared/theme/studio/stackScreenShell';
import { CHECKOUT_CHIP_RADIUS, CHECKOUT_FOOTER_VERTICAL_PAD, checkoutFooterScrollPadding } from '../checkout/components/checkoutLayout';

type UpgradeLine = UpgradableFoodType & { quantity: number };

const HESABE_PAYMENT_TYPES = [
  { id: 1, name: 'KNET' },
  { id: 9, name: 'Apple Pay' },
  { id: 2, name: 'Visa/MasterCard' },
] as const;

const ANDROID_TEXT = Platform.OS === 'android' ? { includeFontPadding: false } : {};

function createUpgradeCheckoutStyles(c: ThemeColors) {
  const card = {
    ...studioCardShell(c),
    borderRadius: DASH_STUDIO_RADIUS,
    backgroundColor: c.surface,
  };

  return StyleSheet.create({
    screen: { flex: 1 },
    content: { flex: 1 },
    scrollContent: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      gap: STACK_SECTION_GAP,
    },
    alertCard: {
      ...card,
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: hexToRgba(c.alert, 0.12) as string,
      padding: appTheme.spacing.md,
      gap: 10,
    },
    alertText: {
      flex: 1,
      fontSize: androidScaleFont(13),
      color: c.alert,
      lineHeight: androidScaleFont(18),
      fontWeight: '600',
      ...ANDROID_TEXT,
    },
    sectionTitle: {
      fontSize: androidScaleFont(13),
      fontWeight: '700',
      color: c.textSecondary,
      letterSpacing: -0.1,
      ...ANDROID_TEXT,
    },
    itemCard: {
      ...card,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: appTheme.spacing.md,
      gap: appTheme.spacing.sm,
    },
    itemInfo: { flex: 1, minWidth: 0, gap: 2 },
    itemName: {
      fontSize: androidScaleFont(15),
      fontWeight: '700',
      color: c.text,
      ...ANDROID_TEXT,
    },
    itemPrice: {
      fontSize: androidScaleFont(13),
      color: c.textSecondary,
      fontWeight: '500',
      ...ANDROID_TEXT,
    },
    itemTotal: {
      fontSize: androidScaleFont(12),
      color: c.primary,
      fontWeight: '700',
      marginTop: 2,
      ...ANDROID_TEXT,
    },
    quantityControls: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      flexShrink: 0,
    },
    quantityBtn: {
      width: androidScaleSize(36),
      height: androidScaleSize(36),
      borderRadius: CHECKOUT_CHIP_RADIUS,
      backgroundColor: hexToRgba(c.text, 0.06) as string,
      overflow: 'hidden',
    },
    quantityBtnContent: {
      flex: 1,
      width: '100%',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
    },
    quantityBtnDisabled: { opacity: 0.4 },
    quantityText: {
      fontSize: androidScaleFont(17),
      fontWeight: '800',
      color: c.text,
      minWidth: 24,
      textAlign: 'center',
      fontVariant: ['tabular-nums'],
      ...ANDROID_TEXT,
    },
    paymentCard: {
      ...card,
      flexDirection: 'row',
      alignItems: 'center',
      padding: appTheme.spacing.md,
      gap: 12,
    },
    paymentCardSelected: {
      backgroundColor: hexToRgba(c.primary, 0.08) as string,
    },
    paymentLeft: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      minWidth: 0,
    },
    radio: {
      width: 20,
      height: 20,
      borderRadius: 10,
      borderWidth: 2,
      borderColor: c.border,
      justifyContent: 'center',
      alignItems: 'center',
    },
    radioSelected: { borderColor: c.primary },
    radioDot: {
      width: 10,
      height: 10,
      borderRadius: 5,
      backgroundColor: c.primary,
    },
    paymentName: {
      flex: 1,
      fontSize: androidScaleFont(15),
      fontWeight: '600',
      color: c.text,
      ...ANDROID_TEXT,
    },
    hesabeTypesContainer: {
      ...card,
      padding: appTheme.spacing.md,
      gap: appTheme.spacing.sm,
    },
    hesabeTypesTitle: {
      fontSize: androidScaleFont(12),
      fontWeight: '700',
      color: c.textSecondary,
      ...ANDROID_TEXT,
    },
    hesabeTypesRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    hesabeTypeChip: {
      paddingHorizontal: 12,
      paddingVertical: 8,
      borderRadius: CHECKOUT_CHIP_RADIUS,
      borderWidth: StyleSheet.hairlineWidth * 2,
      borderColor: c.border,
      backgroundColor: c.surface,
    },
    hesabeTypeChipSelected: {
      borderColor: c.primary,
      backgroundColor: hexToRgba(c.primary, 0.1) as string,
    },
    hesabeTypeText: {
      fontSize: androidScaleFont(13),
      fontWeight: '700',
      color: c.text,
      ...ANDROID_TEXT,
    },
    hesabeTypeTextSelected: { color: c.primary },
    footer: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      paddingTop: CHECKOUT_FOOTER_VERTICAL_PAD,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: hexToRgba(c.text, 0.14) as string,
      backgroundColor: c.surface,
      gap: appTheme.spacing.xs,
      ...androidFlatSurfaceStyle(),
    },
    totalRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: appTheme.spacing.md,
    },
    totalLabel: {
      fontSize: androidScaleFont(12),
      fontWeight: '700',
      color: c.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: 0.55,
      ...ANDROID_TEXT,
    },
    totalValue: {
      fontSize: androidScaleFont(22),
      fontWeight: '800',
      color: c.primary,
      letterSpacing: -0.4,
      ...ANDROID_TEXT,
    },
  });
}

export const UpgradeCheckoutScreen: React.FC = () => {
  const navigation = useAuthNavigation();
  const on_back_exit = useExitOrGoBack();
  const route = useRoute();
  const { t } = useTranslation();
  const user = useAuthStore(s => s.user);
  const themeColors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createUpgradeCheckoutStyles(themeColors), [themeColors]);
  const insets = useSafeAreaInsets();
  const content_top = useHeaderTopPadding(false, appTheme.spacing.sm);
  const { scrollY, onScroll } = useScreenHeaderScroll();
  const { organization } = useApiConfig();

  const subscription = ((route.params as { subscription?: SubscriptionData } | undefined)?.subscription ??
    null) as SubscriptionData | null;
  const route_remaining = Number((route.params as { remainingDays?: number } | undefined)?.remainingDays);
  const remaining_days = useMemo(() => {
    const from_sub = getSubscriptionUpgradeDayLeft(subscription);
    if (from_sub > 0) {
      return from_sub;
    }
    if (Number.isFinite(route_remaining) && route_remaining > 0) {
      return Math.floor(route_remaining);
    }
    return 0;
  }, [subscription, route_remaining]);

  const catalogue_items = useMemo(() => getSubscriptionUpgradableFoodTypes(subscription), [subscription]);

  const [lines, set_lines] = useState<UpgradeLine[]>([]);
  const [is_processing, set_is_processing] = useState(false);
  const [selected_payment, set_selected_payment] = useState('');
  const [payment_type, set_payment_type] = useState(1);

  useEffect(() => {
    set_lines(catalogue_items.map(item => ({ ...item, quantity: 0 })));
  }, [catalogue_items]);

  const payment_gateway_array = useMemo(() => organization?.payment_gateway || [], [organization?.payment_gateway]);
  const payment_mode = useMemo(
    () => resolveOrganizationPaymentMode(organization?.payment_mode),
    [organization?.payment_mode],
  );
  const payment_methods = useMemo(() => {
    if (!Array.isArray(payment_gateway_array) || payment_gateway_array.length === 0) {
      return [];
    }
    return payment_gateway_array.map((gateway: { name?: string; display_name?: string }) => {
      const gateway_name = gateway.name?.toLowerCase() || '';
      let icon = 'card';
      if (gateway_name.includes('hesabe') || gateway_name.includes('tabby')) {
        icon = 'wallet';
      }
      return {
        id: gateway.name || '',
        name: gateway.name || '',
        displayName: gateway.display_name || gateway.name || '',
        icon,
      };
    }).filter(m => m.id);
  }, [payment_gateway_array]);

  const can_pay_online = payment_mode !== 'manual' && payment_methods.length > 0;
  const is_hesabe_gateway =
    typeof selected_payment === 'string' && selected_payment.toLowerCase().includes('hesabe');
  const show_payment_gateway_choice = payment_methods.length > 1;
  const show_hesabe_payment_type_picker = is_hesabe_gateway && payment_methods.length > 1;
  const show_payment_section =
    can_pay_online && payment_methods.length > 0 && (show_payment_gateway_choice || show_hesabe_payment_type_picker);

  useEffect(() => {
    if (payment_methods.length === 0) {
      return;
    }
    const only_id = payment_methods.length === 1 ? payment_methods[0].id : null;
    if (!selected_payment) {
      set_selected_payment(payment_methods[0].id);
      return;
    }
    if (only_id != null && selected_payment !== only_id) {
      set_selected_payment(only_id);
    }
  }, [payment_methods, selected_payment]);

  const line_cost = useCallback(
    (item: UpgradeLine) => item.quantity * item.price_per_item * remaining_days,
    [remaining_days],
  );
  const total_cost = lines.reduce((sum, item) => sum + line_cost(item), 0);
  const has_selection = lines.some(item => item.quantity > 0);
  const upgrade_allowed = canUpgradeSubscription(subscription) || catalogue_items.length > 0;

  const add_item = (item_id: number) => {
    set_lines(prev =>
      prev.map(item =>
        item.id === item_id && item.quantity < item.max_quantity
          ? { ...item, quantity: item.quantity + 1 }
          : item,
      ),
    );
  };

  const remove_item = (item_id: number) => {
    set_lines(prev =>
      prev.map(item =>
        item.id === item_id && item.quantity > 0 ? { ...item, quantity: item.quantity - 1 } : item,
      ),
    );
  };

  const proceed_to_payment = async () => {
    if (!has_selection) {
      Alert.alert(t('checkout.noSelection'), t('checkout.selectOneItemToUpgrade'));
      return;
    }
    if (!subscription?.id || typeof user?.id !== 'number') {
      Alert.alert(t('common.error'), t('checkout.failedToProcessUpgrade'));
      return;
    }
    if (can_pay_online && !selected_payment) {
      Alert.alert(t('common.error'), t('checkout.selectPaymentMethod') || 'Please select a payment method');
      return;
    }

    set_is_processing(true);
    try {
      const payload: Record<string, unknown> = {
        customer_id: user.id,
        customer_subscription_id: subscription.id,
        /** API validates this key (not `items`) — same shape as legacy Ionic. */
        upgradable_food_types: buildUpgradeFoodTypesPayload(lines),
        payment_type: is_hesabe_gateway ? payment_type : 1,
      };
      if (can_pay_online) {
        payload.payment_gateway = selected_payment;
      }

      const response = await postUpgradeSubscription(payload);
      const charge_url = response?.data?.charge_url;

      if (response?.success && charge_url) {
        navigation.navigate('PaymentWebView', {
          url: charge_url,
          subscriptionId: subscription.id,
          flow: 'subscription',
        });
      } else if (response?.success && !charge_url && !can_pay_online) {
        Alert.alert(t('common.success') || 'Success', response.message || t('checkout.subscriptionCreated'));
        navigation.navigate('MainTabs');
      } else {
        Alert.alert(t('common.error'), response?.message || t('checkout.failedToProcessUpgrade'));
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('checkout.anErrorOccurred');
      Alert.alert(t('common.error'), message);
    } finally {
      set_is_processing(false);
    }
  };

  const footer_scroll_pad = useMemo(() => checkoutFooterScrollPadding(insets.bottom), [insets.bottom]);
  const footer_bottom_pad = Math.max(insets.bottom, appTheme.spacing.sm);
  const days_word =
    remaining_days === 1 ? t('checkout.day') || 'day' : t('subscriptions.daysLabel') || 'days';

  if (!subscription) {
    return (
      <StackScreenShell colors={themeColors}>
        <View style={styles.screen}>
          <ScreenHeader title={t('checkout.upgradeTitle')} onBack={on_back_exit} scrollY={scrollY} />
          <EmptyState
            icon="upgrade"
            title={t('checkout.noUpgradeOptionsAvailable')}
            message={t('checkout.failedToProcessUpgrade')}
          />
        </View>
      </StackScreenShell>
    );
  }

  return (
    <StackScreenShell colors={themeColors}>
      <View style={styles.screen}>
        <ScreenHeader title={t('checkout.upgradeTitle')} onBack={on_back_exit} scrollY={scrollY} />

        <Animated.ScrollView
          style={styles.content}
          onScroll={onScroll}
          scrollEventThrottle={16}
          contentContainerStyle={[
            styles.scrollContent,
            { paddingTop: content_top, paddingBottom: footer_scroll_pad },
          ]}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled">
          <View style={styles.alertCard}>
            <SvgIcon name="info" size={20} color={themeColors.alert} />
            <Text style={styles.alertText}>
              {t('checkout.upgradesApplyRemaining')}
              {formatNumber(remaining_days)} {days_word} {t('checkout.ofYourSubscription')}
            </Text>
          </View>

          <Text style={styles.sectionTitle}>{t('checkout.selectItemsToAdd') || t('checkout.addons')}</Text>

          {lines.map(item => {
            const at_max = item.quantity >= item.max_quantity;
            const at_min = item.quantity <= 0;
            const display_name =
              getApiText(item as unknown as Record<string, unknown>, 'name', 'a_name') || item.name;
            return (
              <View key={item.id} style={styles.itemCard}>
                <View style={styles.itemInfo}>
                  <Text style={styles.itemName} numberOfLines={2}>
                    {display_name}
                  </Text>
                  <Text style={styles.itemPrice}>
                    {formatCurrency(item.price_per_item)} / {t('checkout.day') || 'day'}
                  </Text>
                  {item.quantity > 0 ? (
                    <Text style={styles.itemTotal}>{formatCurrency(line_cost(item))}</Text>
                  ) : null}
                </View>
                <View style={styles.quantityControls}>
                  <Clickable
                    style={[styles.quantityBtn, at_min && styles.quantityBtnDisabled]}
                    contentStyle={styles.quantityBtnContent}
                    onPress={() => remove_item(item.id)}
                    disabled={at_min}
                    accessibilityRole="button"
                    accessibilityLabel={`${t('common.decrease') || 'Decrease'} ${display_name}`}>
                    <SvgIcon name="minus" size={18} color={themeColors.text} />
                  </Clickable>
                  <Text style={styles.quantityText}>{formatNumber(item.quantity)}</Text>
                  <Clickable
                    style={[styles.quantityBtn, at_max && styles.quantityBtnDisabled]}
                    contentStyle={styles.quantityBtnContent}
                    onPress={() => add_item(item.id)}
                    disabled={at_max}
                    accessibilityRole="button"
                    accessibilityLabel={`${t('common.increase') || 'Increase'} ${display_name}`}>
                    <SvgIcon name="plus" size={18} color={themeColors.text} />
                  </Clickable>
                </View>
              </View>
            );
          })}

          {lines.length === 0 ? (
            <EmptyState icon="upgrade" title={t('checkout.noUpgradeOptionsAvailable')} message="" />
          ) : null}

          {show_payment_section ? (
            <>
              <Text style={styles.sectionTitle}>{t('checkout.paymentMethod')}</Text>
              {show_payment_gateway_choice
                ? payment_methods.map(method => {
                    const is_selected = selected_payment === method.id;
                    return (
                      <Clickable
                        key={method.id}
                        style={[styles.paymentCard, is_selected && styles.paymentCardSelected]}
                        onPress={() => set_selected_payment(method.id)}
                        accessibilityRole="radio"
                        accessibilityState={{ selected: is_selected }}>
                        <View style={styles.paymentLeft}>
                          <View style={[styles.radio, is_selected && styles.radioSelected]}>
                            {is_selected ? <View style={styles.radioDot} /> : null}
                          </View>
                          <SvgIcon
                            name={method.icon as any}
                            size={20}
                            color={is_selected ? themeColors.primary : themeColors.textSecondary}
                          />
                          <Text style={styles.paymentName} numberOfLines={1}>
                            {method.displayName || method.name}
                          </Text>
                        </View>
                        {is_selected ? <SvgIcon name="check" size={18} color={themeColors.primary} /> : null}
                      </Clickable>
                    );
                  })
                : null}
              {show_hesabe_payment_type_picker ? (
                <View style={styles.hesabeTypesContainer}>
                  <Text style={styles.hesabeTypesTitle}>{t('checkout.selectPaymentType')}</Text>
                  <View style={styles.hesabeTypesRow}>
                    {HESABE_PAYMENT_TYPES.map(type => (
                      <Clickable
                        key={type.id}
                        style={[styles.hesabeTypeChip, payment_type === type.id && styles.hesabeTypeChipSelected]}
                        onPress={() => set_payment_type(type.id)}>
                        <Text
                          style={[
                            styles.hesabeTypeText,
                            payment_type === type.id && styles.hesabeTypeTextSelected,
                          ]}>
                          {type.name}
                        </Text>
                      </Clickable>
                    ))}
                  </View>
                </View>
              ) : null}
            </>
          ) : null}
        </Animated.ScrollView>

        <View style={[styles.footer, { paddingBottom: footer_bottom_pad }]}>
          <View style={styles.totalRow}>
            <Text style={styles.totalLabel}>{t('order.total')}</Text>
            <Text style={styles.totalValue}>{formatCurrency(total_cost)}</Text>
          </View>
          <SolidCtaClickable
            label={
              can_pay_online
                ? t('checkout.payNow') || 'Pay now'
                : t('checkout.requestSubscription') || 'Request upgrade'
            }
            onPress={() => void proceed_to_payment()}
            disabled={!has_selection || !upgrade_allowed || is_processing || remaining_days <= 0}
            loading={is_processing}
            variant="primary"
            haptics
            accessibilityLabel={t('checkout.payNow')}
          />
        </View>
      </View>
    </StackScreenShell>
  );
};

export default UpgradeCheckoutScreen;
